import type { ReactNode } from 'react';
import styles from './StatCard.module.css';

export interface StatCardProps {
  /** Mono small-caps label above the value (e.g. "PENDING INVITES"). */
  label: ReactNode;
  /** The large value (number or short string). */
  value: ReactNode;
  /** Optional small caption beneath the value (e.g. "1 expires in 2d"). */
  caption?: ReactNode;
  /** Optional click handler — when set, the card becomes a button (e.g. clicking "Pending invites" filters the table). */
  onClick?: () => void;
  /** Optional extra class. */
  className?: string;
}

/**
 * Compact metric card used in the Users page stat strip + every future admin
 * surface that wants to surface a small set of headline numbers (e.g. KB
 * publication counts, benchmark contributor counts).
 *
 * Sizes: fixed at the design-system "stat card" size — label mono xs above,
 * value 28px below, optional caption mono xs muted. Card padding = s4.
 */
export function StatCard({ label, value, caption, onClick, className }: StatCardProps) {
  const classes = [styles.card, onClick ? styles.clickable : null, className]
    .filter(Boolean)
    .join(' ');

  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>{value}</span>
        {caption && <span className={styles.caption}>{caption}</span>}
      </button>
    );
  }

  return (
    <div className={classes}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value}</span>
      {caption && <span className={styles.caption}>{caption}</span>}
    </div>
  );
}
